import { navigate } from 'gatsby';
import { find, map, propEq } from 'ramda';
import React, { FC, ReactNode, useMemo } from 'react';
import { formatPhoneNumber } from 'react-phone-number-input';
import styled from 'styled-components';

import BaseCard from '@ui/atoms/BaseCard';
import BasicText from '@ui/atoms/BasicText';
import Button from '@ui/atoms/Button';
import { DashboardCardLayout } from '@ui/atoms/DashboardCardLayout';
import { DashboardInfoDisplay } from '@ui/atoms/DashboardInfoDisplay';
import { DashboardInfoSmallText } from '@ui/atoms/DashboardInfoSmallText';
import { EditButtonWrapper } from '@ui/atoms/EditButtonWrapper';
import FlexColumn from '@ui/atoms/FlexColumn';
import HeadingText from '@ui/atoms/HeadingText';
import LoadingWrapper from '@ui/atoms/LoadingWrapper';
import Skeleton from '@ui/atoms/Skeleton';
import Tooltip from '@ui/atoms/Tooltip';

import ProfileDropdown from '@ui/organisms/ProfileDropdown';

import fullWidth from '@ui/styled/FullWidth';

import { doesNotExist, exists } from '@utils/general';

import relationships from '@data/relationships';

import { IEmergencyContact, IMemberData } from '@folx-types/member';

import Selfie from '@assets/vectors/selfie-upload.inline.svg';

import theme from '@theme/FolxTheme';

export interface IAccountInfoContent {
	butterPage: {
		heading: string;
		profilePictureLabel: string;
		nameLabel: string;
		pronounsLabel: string;
		emailLabel: string;
		phoneNumberLabel: string;
		smsOptInLabel: string;
		innerspaceLabel: string;
		governmentIdLabel: string;
		ssnLabel: string;
		legalNameLabel: string;
		emergencyLabel: string;
		emailUsDisclaimer: string;
		emptyContactsText: string;
		contactTooltipTitle: string;
		contactTooltipDescription: string;
	};
}

interface IAccountInfoProps {
	pageContent: IAccountInfoContent;
	loading: boolean;
	emergencyContacts?: IEmergencyContact[];
	idPhoto?: string;
	isPdf?: boolean;
	member?: IMemberData;
	selfie?: string;
}

const ProfilePhoto = styled.div`
	height: 15rem;
	width: 15rem;
	margin-top: 0.8rem;
	border-radius: 50%;
	background-color: ${theme.colors.lightGray};
	display: flex;
	align-items: center;
	justify-content: center;
	overflow: hidden;
	img {
		height: 100%;
		width: 100%;
		object-fit: cover;
	}
`;

const Placeholder = styled(Selfie)`
	height: 50%;
	width: 50%;
`;

const IDWrapper = styled.div<{ isPdf?: boolean }>`
	display: flex;
	flex-direction: row;
	align-items: center;
	text-align: center;
	border: solid 1px ${theme.colors.black};
	border-radius: ${theme.radius.xxl};
	gap: ${theme.spacing.md};
	overflow: hidden;
	height: 7.8rem;
	width: ${({ isPdf = false }) => (isPdf ? 'fit-content' : '10.5rem')};

	img {
		object-fit: cover;
		width: 100%;
		height: 100%;
	}

	${BasicText} {
		padding: 0 ${theme.spacing.md};
	}
`;

const Disclaimer = styled(FlexColumn)`
	padding: 0 ${theme.spacing.md};
	align-items: center;
`;

const ContactTooltip = styled(Tooltip)`
	width: fit-content;
	align-self: flex-end;
	padding: 0;

	& > div {
		max-width: 21rem;
	}

	@media ${theme.responsive.breakpoints.tablet} {
		align-self: flex-start;
		margin-bottom: ${theme.spacing.sm};

		& > div {
			max-width: 24rem;
		}
	}
`;

const InfoContainer = styled.div`
	overflow-wrap: anywhere;
`;

const FullWidthFlexColumn = styled(fullWidth(FlexColumn))`
	display: flex;
`;

const ContactSkeleton = styled(Skeleton)`
	width: 100%;
	@media ${theme.responsive.breakpoints.tablet} {
		width: 40rem;
	}
`;

const AccountInfo: FC<IAccountInfoProps> = ({
	pageContent,
	emergencyContacts = [],
	member,
	loading,
	idPhoto,
	selfie,
	isPdf,
}) => {
	const {
		butterPage: {
			heading,
			profilePictureLabel,
			nameLabel,
			pronounsLabel,
			emailLabel,
			phoneNumberLabel,
			smsOptInLabel,
			innerspaceLabel,
			governmentIdLabel,
			ssnLabel,
			legalNameLabel,
			emergencyLabel,
			emailUsDisclaimer,
			emptyContactsText,
			contactTooltipTitle,
			contactTooltipDescription,
		},
	} = pageContent;

	const getFirstName = () => member?.affirmingFirstName ?? member?.legalFirstName;
	const getLastName = () => member?.affirmingLastName ?? member?.legalLastName;
	const fullName = `${getFirstName()} ${getLastName()}`;

	const InfoLine = ({
		className = '',
		label,
		text,
		loading,
		skeleton,
	}: {
		label: string;
		className?: string;
		text?: string;
		loading: boolean;
		skeleton?: ReactNode;
	}) => (
		<InfoContainer>
			<DashboardInfoSmallText variant="field">{label}</DashboardInfoSmallText>
			<LoadingWrapper
				loading={loading}
				skeleton={skeleton ?? <Skeleton height="md" fullWidth structure="single" />}>
				<BasicText className={className}>{text}</BasicText>
			</LoadingWrapper>
		</InfoContainer>
	);

	const BasicAccountInfoCard = (
		<DashboardInfoDisplay>
			<FlexColumn gap="smmd">
				<div>
					<DashboardInfoSmallText variant="field">{profilePictureLabel}</DashboardInfoSmallText>
					{/* butterify this label */}
					<ProfilePhoto>
						<LoadingWrapper
							loading={loading}
							skeleton={<Skeleton borderRadius="half" structure="single" fullHeight fullWidth />}>
							{selfie ? <img src={selfie} alt="your beautiful face" /> : <Placeholder />}
						</LoadingWrapper>
					</ProfilePhoto>
				</div>
				<InfoLine loading={loading} label={nameLabel} text={fullName} />
				{/* butterify this label */}
				<InfoLine
					label={pronounsLabel}
					loading={loading}
					text={
						member?.preferredPronouns && member.preferredPronouns.length > 0
							? member?.preferredPronouns?.join(', ')
							: 'Not provided'
					}
				/>
				<InfoLine label={emailLabel} text={member?.email} loading={loading} />
				<InfoLine
					label={phoneNumberLabel}
					loading={loading}
					text={
						member?.smsNumber
							? exists(formatPhoneNumber(member?.smsNumber))
								? formatPhoneNumber(member?.smsNumber)
								: member?.smsNumber
							: 'Not provided'
					}
				/>
				<InfoLine
					loading={loading}
					label={smsOptInLabel}
					text={member?.smsContactConsent ? 'Yes' : 'No'}
				/>{' '}
				{/* butterify this label */}
				<InfoLine loading={loading} label={innerspaceLabel} text={member?.nickname || 'N/A'} />
			</FlexColumn>
			<EditButtonWrapper>
				<Button disabled={loading} label="Edit" onClick={() => navigate('./info_edit')} />
			</EditButtonWrapper>
		</DashboardInfoDisplay>
	);

	const GovernmentIDCard = () => (
		<IDWrapper isPdf={isPdf}>
			{isPdf ? <BasicText>PDF Upload</BasicText> : <img src={idPhoto} alt="your governement ID" />}
		</IDWrapper>
	);

	const IdentificationAccountInfoCard = (
		<DashboardInfoDisplay>
			<FlexColumn gap="smmd">
				{/* butterify this label */}
				<div>
					<DashboardInfoSmallText variant="field">{governmentIdLabel}</DashboardInfoSmallText>
					<GovernmentIDCard />
				</div>
				<InfoLine
					loading={loading}
					label={ssnLabel}
					text={
						member?.ssn && member?.ssn !== '000-00-0000'
							? `***-**-${member?.ssn?.slice(7)}`
							: 'Not provided'
					}
				/>
				{/* ^ butterify this label */}
				<InfoLine
					loading={loading}
					label={legalNameLabel}
					text={
						member?.legalFirstName && member?.legalLastName
							? `${member?.legalFirstName} ${member?.legalLastName}`
							: 'Not provided'
					}
				/>
			</FlexColumn>
			<EditButtonWrapper>
				<Button disabled={loading} label="Edit" onClick={() => navigate('./legal_edit')} />
			</EditButtonWrapper>
		</DashboardInfoDisplay>
	);

	const EmergencyContactsCard = useMemo(
		() => (
			<DashboardInfoDisplay>
				<FullWidthFlexColumn>
					<ContactTooltip
						title={contactTooltipTitle}
						description={contactTooltipDescription}
						defaultIcon
					/>
					<DashboardInfoSmallText variant="field">{emergencyLabel}</DashboardInfoSmallText>
					<LoadingWrapper
						loading={loading}
						skeleton={<ContactSkeleton height="md" gap="xs" structure="column" repeated={3} />}>
						<>
							{doesNotExist(emergencyContacts) && <BasicText>{emptyContactsText}</BasicText>}
							{exists(emergencyContacts) && (
								<FlexColumn gap="lg">
									{map((contact) => {
										const relationship = find<{ value: string; label: string }>(
											propEq('value', contact.relationship)
										)(relationships);

										const phoneNumber = exists(formatPhoneNumber(contact.phoneNumber))
											? formatPhoneNumber(contact.phoneNumber)
											: contact.phoneNumber;

										return (
											<FlexColumn gap="xxxs" key={contact.id}>
												<BasicText>
													{contact.firstName} {contact.lastName}
												</BasicText>
												<BasicText>{relationship?.label}</BasicText>
												<BasicText>{phoneNumber}</BasicText>
												{exists(contact.email) && <BasicText>{contact.email}</BasicText>}
											</FlexColumn>
										);
									}, emergencyContacts)}
								</FlexColumn>
							)}
						</>
					</LoadingWrapper>
				</FullWidthFlexColumn>
				<EditButtonWrapper>
					<Button disabled={loading} label="Edit" onClick={() => navigate('./contacts')} />
				</EditButtonWrapper>
			</DashboardInfoDisplay>
		),
		[emergencyContacts, loading]
	);

	return (
		<DashboardCardLayout>
			<ProfileDropdown />
			<HeadingText>{heading}</HeadingText>
			<BaseCard bgColor="white" body={BasicAccountInfoCard} borderless radius="md" shadow />
			<BaseCard
				bgColor="white"
				body={IdentificationAccountInfoCard}
				borderless
				radius="md"
				shadow
			/>
			<BaseCard bgColor="white" body={EmergencyContactsCard} borderless radius="md" shadow />
			<Disclaimer>
				<BaseCard
					borderless
					body={<DashboardInfoSmallText>{emailUsDisclaimer}</DashboardInfoSmallText>}
				/>
			</Disclaimer>
		</DashboardCardLayout>
	);
};

export default AccountInfo;
